import * as React from 'react';
import { useBetslipContext } from '~/vendor/cx-web/feature-betslip/context';
import { useLabelActions } from '~/vendor/cx-web/feature-i18n';

import styles from '../style/clickout-error-modal.module.scss';

/**
 * Error modal shown when a "Place Bet" clickout is blocked (see
 * handleClickoutToPlaceBet in betslip-context.tsx). Previously these failures were
 * swallowed with a console.error, so the button appeared broken to the user.
 *
 * The modal deliberately shows only a generic message plus an opaque reference
 * code (e.g. `BSX-1A2B`) — never the internal cause, which would leak config
 * detail. The code maps back to the real reason via CLICKOUT_ERROR_LOOKUP for
 * support/engineers. Labels fall back to English so the modal still reads
 * correctly before the CMS supplies localized copy.
 */
const ClickoutErrorModal = () => {
  const labelActions = useLabelActions();
  const { clickoutErrorCode, dismissClickoutError } = useBetslipContext();

  const dismissRef = React.useRef<HTMLButtonElement>(null);

  // Move focus to the dismiss button when the modal appears so keyboard/screen-reader
  // users land inside the dialog, and restore focus to the trigger on close.
  React.useEffect(() => {
    if (!clickoutErrorCode) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    dismissRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissClickoutError();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [clickoutErrorCode, dismissClickoutError]);

  if (!clickoutErrorCode) return null;

  const title = labelActions.get('BETSLIP_ERROR_TITLE') || 'Something went wrong';
  const message =
    labelActions.get('BETSLIP_ERROR_MESSAGE') ||
    "We couldn't open this bet right now. Please try again later or pick another bookmaker.";
  const referenceLabel = labelActions.get('BETSLIP_ERROR_REFERENCE') || 'Reference';
  const dismissLabel = labelActions.get('BETSLIP_ERROR_DISMISS') || 'Close';

  return (
    <div className={styles.overlay} data-testid="betslip-clickout-error">
      <div
        aria-describedby="betslip-clickout-error-message"
        aria-labelledby="betslip-clickout-error-title"
        aria-modal="true"
        className={styles.modal}
        role="alertdialog"
      >
        <h2 className={styles.title} id="betslip-clickout-error-title">
          {title}
        </h2>
        <p className={styles.message} id="betslip-clickout-error-message">
          {message}
        </p>
        <p className={styles.reference}>
          {referenceLabel}: <span className={styles.code}>{clickoutErrorCode}</span>
        </p>
        <button className={styles.dismiss} onClick={dismissClickoutError} ref={dismissRef} type="button">
          {dismissLabel}
        </button>
      </div>
    </div>
  );
};

export default ClickoutErrorModal;
